import { Component, OnInit, AfterViewInit, OnDestroy, Input, ElementRef, inject, signal, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { forkJoin, of, map, Observable, firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { AccordionModule } from 'primeng/accordion';
import { DatePickerModule } from 'primeng/datepicker';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { ReviewService, ReviewType, Review } from '../../services/review.service';
import { ReviewControlService, ReviewControl, ReviewControlDocument, ReviewControlRequest } from '../../services/review-control.service';
import { DocumentsManagerComponent, DocManagerItem } from '../documents-manager/documents-manager.component';
import { NetworkService, NetworkBodyShop, AddNetworkBodyShopRequest } from '../../../core/services/network.service';
import { 
  RegistrationStepService, 
  RegistrationStepResponse,
  RegistrationStatus 
} from '../../../core/services/registration-step.service';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../core/services/geography.service';
import { ProfessionalRegistryItem } from '../../../core/models/professional.models';
import { ProfessionalsGeneralDataComponent } from '../../../frontoffice/components/professionals-personal-info/professionals-general-data/professionals-general-data.component';
import { ProfessionalsSpecificDataComponent } from '../../../frontoffice/components/professionals-personal-info/professionals-specific-data/professionals-specific-data.component';
import { ProfessionalsQuestionnaireAttachmentsComponent } from '../../../frontoffice/components/professionals-personal-info/professionals-questionnaire-attachments/professionals-questionnaire-attachments.component';
import { ProfessionalsCollaboratorsComponent } from '../../../frontoffice/components/professionals-personal-info/professionals-collaborators/professionals-collaborators.component';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ProfessionalOperationalDataComponent } from '../axa-professional-data/axa-professional-data.component';
import { SolCodesManagerComponent } from '../sol-codes-manager/sol-codes-manager.component';
import { SolCodeService, SolCodeMfa } from '../../services/sol-code.service';
import { ContractRegistryService } from '../../services/contract-registry.service';
import { DocumentType, MainDocument } from '../../models/contract.model';
import { ColumnConfig } from '../dashboard/table-config';
import { FileValidationService } from '../../../core/services/file-validation.service';
import { DocumentsService } from '../../../core/services/documents.service';
import { Alert } from '../../../core/models/models';

/**
 * Esito di un controllo (Cribis / World Check). Valore condiviso FE/BE: OK | KO | IN_PROGRESS.
 * L'etichetta UI per IN_PROGRESS è "Approfondimenti in corso".
 */
export type CheckOutcome = 'OK' | 'KO' | 'IN_PROGRESS';
/** Strumento di controllo cui si riferisce l'esito. */
export type CheckKind = 'cribis' | 'wc';

@Component({
  selector: 'app-review-professional',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    AccordionModule,
    DatePickerModule,
    ToastModule,
    MessageModule,
    TabsModule,
    TextareaModule,
    DialogModule,
    TooltipModule,
    ProfessionalsGeneralDataComponent,
    ProfessionalsSpecificDataComponent,
    ProfessionalsQuestionnaireAttachmentsComponent,
    ProfessionalsCollaboratorsComponent,
    RegistryTableComponent,
    ProfessionalOperationalDataComponent,
    SolCodesManagerComponent,
    ConfirmDialogModule,
    CheckboxModule,
    InputTextModule,
    RouterLink,
    DocumentsManagerComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './review-professional.component.html',
  styleUrls: ['./review-professional.component.scss']
})
export class ReviewProfessionalComponent implements OnInit, AfterViewInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private reviewService = inject(ReviewService);
  private reviewControlService = inject(ReviewControlService);
  private messageService = inject(MessageService);
  private registrationStepService = inject(RegistrationStepService);
  private professionalsService = inject(ProfessionalsService);
  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);
  private contractService = inject(ContractRegistryService);
  private solCodeService = inject(SolCodeService);
  private documentsService = inject(DocumentsService);
  private fileValidationService = inject(FileValidationService);
  private titleService = inject(Title);
  private networkService = inject(NetworkService);
  private confirmationService = inject(ConfirmationService);
  private hostElement = inject(ElementRef<HTMLElement>);
  private readonly textSelectableInputTypes = new Set(['text', 'email', 'number', 'search', 'tel', 'url']);
  private selectionObserver: MutationObserver | null = null;

  @ViewChild(ProfessionalOperationalDataComponent) operationalDetailsComp?: ProfessionalOperationalDataComponent;
  @ViewChild(SolCodesManagerComponent) solCodesManagerComp?: SolCodesManagerComponent;

  /**
   * Component mode:
   * - 'review': Full review mode with action buttons (approve/reject)
   * - 'detail': Read-only detail view (no action buttons)
   */
  @Input() mode: 'review' | 'detail' = 'review';

  // Professional ID from route
  professionalId = signal<string>('');
  
  // Professional data (partial data from summary API)
  professionalData = signal<Partial<ProfessionalRegistryItem> | null>(null);
  isLoadingProfessional = signal<boolean>(true);
  reviewAlerts = signal<Alert[]>([]);

  // Current registration step
  currentStep = signal<string>('');
  parentProfessionalId = signal<string | null>(null);
  parentInfo = signal<{ businessName: string; identificationCode: string; address: string } | null>(null);

  // Computed signals based on current step (not queryParam)
  // Review mode: currentStep === 'VERIFICATION' (pending BO review)
  // Detail mode: any other step (read-only view)
  isReviewMode = computed(() => this.currentStep() === 'VERIFICATION');
  isDetailMode = computed(() => this.currentStep() !== 'VERIFICATION' && this.currentStep() !== '');
  
  // Contratto "in fase di definizione" = bozza (DRAFT) o generato/da attivare (GENERATED).
  // Ne è ammesso al massimo uno per fiduciario (limite lato FE + BE).
  inDefinitionContract = computed(
    () =>
      this.contractsData().find((c) => c?.status === 'DRAFT' || c?.status === 'GENERATED') ?? null,
  );
  hasInDefinitionContract = computed(() => this.inDefinitionContract() !== null);

  // Step precedenti alla fase contrattuale: in questi non esistono ancora contratti.
  private static readonly PRE_CONTRACT_STEPS = new Set([
    '',
    'PORTAL_ACCESS',
    'DATA_INPUT',
    'VERIFICATION',
    'MFA_SETUP',
  ]);
  // Dallo step "in attesa di contratto" (CONTRACT_SENT) in poi, incluso il fiduciario attivo.
  isContractStepOrLater = computed(
    () => !ReviewProfessionalComponent.PRE_CONTRACT_STEPS.has(this.currentStep()),
  );

  // Mostra "Crea Contratto" solo in CONTRACT_SENT e finché non esiste già un contratto in definizione.
  shouldShowCreateContractButton = computed(
    () => this.currentStep() === 'CONTRACT_SENT' && !this.hasInDefinitionContract(),
  );
  // In CONTRACT_SENT con un contratto già in definizione: mostra il messaggio-istruzione (no nuova creazione).
  shouldShowContinueContractMessage = computed(
    () => this.currentStep() === 'CONTRACT_SENT' && this.hasInDefinitionContract(),
  );

  // Tabella contratti: dalla fase contrattuale in poi e solo se esiste almeno un contratto.
  shouldShowContractsTable = computed(
    () => this.isContractStepOrLater() && this.contractsData().length > 0,
  );

  // Contratto di riferimento per i link ai box firma/controfirma (unico contratto attivo del flusso).
  primaryContract = computed(() => this.contractsData()[0] ?? null);

  // Fiduciario già attivato: il flusso contrattuale è concluso, nessun banner di attesa.
  isProfessionalActive = computed(() => this.professionalData()?.status === 'ACTIVE');

  // In attesa che il FIDUCIARIO firmi il contratto (evidenza informativa per il BO).
  isAwaitingSignature = computed(
    () => this.currentStep() === 'CONTRACT_SIGNATURE' && !this.isProfessionalActive(),
  );
  // In attesa della CONTROFIRMA di AXA (azione a carico del BO: caricare il doc controfirmato e attivare).
  isAwaitingCountersignature = computed(
    () => this.currentStep() === 'CONTRACT_COUNTERSIGNATURE' && !this.isProfessionalActive(),
  );
  // Documento principale del contratto di riferimento, letto dal dettaglio (la lista non lo espone).
  // s3FileUrl = controfirmato/FINAL, s3FileUrlExternallySigned = firmato dal fiduciario.
  private contractMainDoc = signal<MainDocument | null>(null);
  // Controfirmato caricato (manca solo l'attivazione).
  countersignedDocUploaded = computed(() => !!this.contractMainDoc()?.s3FileUrl);
  // Disponibilità documenti per lo storico registrazione.
  hasSignedContractDoc = computed(() => !!this.contractMainDoc()?.s3FileUrlExternallySigned);
  hasCountersignedContractDoc = computed(() => !!this.contractMainDoc()?.s3FileUrl);

  // Fase contrattuale: MFA confermato, contratto in corso ma fiduciario non ancora attivo.
  // In questa fase l'aggiunta di nuovi codici SOL è bloccata (torna disponibile dopo l'attivazione).
  isContractPhase = computed(() =>
    ['CONTRACT_SENT', 'CONTRACT_SIGNATURE', 'CONTRACT_COUNTERSIGNATURE'].includes(this.currentStep()),
  );

  // ── Fase MFA (Codici SOL) ─────────────────────────────────────────────────
  // Status dello step corrente (serve a disambiguare le sotto-fasi di MFA_SETUP).
  currentStepStatus = signal<string>('');
  // Dopo l'approvazione anagrafica bisogna forzare il refresh dei codici SOL quando si
  // entra nella fase SOL (primo impianto: i codici possono già esistere a BE).
  private reloadSolAfterStep = false;
  // Dati MFA correnti (codici SOL con mail principale/responsabile/associazioni).
  mfaData = signal<SolCodeMfa[]>([]);
  isLoadingMfa = signal<boolean>(false);
  isMfaSubmitting = signal<boolean>(false);
  isExportingMfa = signal<boolean>(false);

  isMfaStep = computed(() => this.currentStep() === 'MFA_SETUP');
  // "Compilato" = tutti i codici SOL hanno mail principale + responsabile valorizzati
  // (mirror del controllo hard lato BE che gestisce le transizioni).
  mfaComplete = computed(() => {
    const data = this.mfaData();
    return data.length > 0 && data.every((m) => m.compiled);
  });
  // True se nel ciclo MFA corrente la compilazione è GIÀ stata richiesta al fiduciario, cioè in
  // storico esiste uno step MFA_SETUP passato in USER_INPUT o MODIFICATION_REQUESTED. È il
  // discriminante corretto tra la PENDING "iniziale" (il BO deve ancora richiedere la
  // compilazione) e la PENDING "post-conferma" (dati inviati dal fiduciario, da revisionare).
  // NB: non si usa mfaComplete perché i primi impianti nascono con SOL già completi
  // (mfaComplete=true da subito): su mfaComplete la pagina mostrerebbe la revisione saltando la
  // richiesta di compilazione, impedendo al fiduciario di agganciare collaboratori/impiegati.
  private mfaCompilationRequested = computed(() =>
    this.stepHistory().some(
      (s) =>
        s.step === 'MFA_SETUP' &&
        (s.status === 'USER_INPUT' || s.status === 'MODIFICATION_REQUESTED'),
    ),
  );
  // BO deve ancora richiedere la compilazione al fiduciario (PENDING iniziale, mai richiesta).
  isMfaRequestMode = computed(
    () => this.isMfaStep() && this.currentStepStatus() === 'PENDING' && !this.mfaCompilationRequested(),
  );
  // BO revisiona i dati compilati e confermati dal fiduciario (PENDING dopo la conferma).
  isMfaReviewMode = computed(
    () => this.isMfaStep() && this.currentStepStatus() === 'PENDING' && this.mfaCompilationRequested(),
  );

  // Fiduciario di primo impianto (import massivo): created_by = 'System_PI'.
  isFirstInstallation = computed(() => this.professionalData()?.createdBy === 'System_PI');
  // Tutti i codici SOL sono già ATTIVI (condizione del primo impianto).
  private allSolCodesActive = computed(() => {
    const data = this.mfaData();
    return data.length > 0 && data.every((s) => s.status === 'ACTIVE');
  });
  // Esiste almeno un codice SOL master.
  private hasMasterSolCode = computed(() => this.mfaData().some((s) => s.master));
  // "Salta MFA" disponibile solo per i primi impianti nella PENDING iniziale (compilazione mai
  // richiesta) con tutti i SOL già attivi e un master: non c'è nulla da far compilare. Il BO
  // valida d'ufficio e si avanza a CONTRACT_SENT. Rispecchia le guardie del BE (POST mfa/skip).
  isMfaSkippable = computed(
    () =>
      this.isMfaRequestMode() &&
      this.isFirstInstallation() &&
      this.allSolCodesActive() &&
      this.hasMasterSolCode(),
  );
  // Compilazione in carico al fiduciario: BO in sola lettura, in attesa.
  isMfaWaitingMode = computed(
    () =>
      this.isMfaStep() &&
      (this.currentStepStatus() === 'USER_INPUT' ||
        this.currentStepStatus() === 'MODIFICATION_REQUESTED'),
  );

  /**
   * Flag persistito (backend): in validazione MFA il BO ha modificato i codici SOL dopo la
   * conferma del fiduciario. La compilazione MFA non è più valida: l'approvazione resta
   * disabilitata anche dopo un reload della pagina finché non si richiede una nuova compilazione.
   */
  mfaReviewDirty = signal<boolean>(false);

  /**
   * In validazione MFA l'approvazione non è disponibile quando la compilazione è stata
   * invalidata da una modifica strutturale dei codici SOL: resta solo "Richiedi Modifica".
   */
  isApproveMfaDisabled = computed(() => this.mfaReviewDirty());

  pageTitle = computed(() => {
    const name = this.displayName();
    const base = this.isReviewMode() ? 'Revisione Fiduciario' : 'Dettaglio Fiduciario';
    return name ? `${base} — ${name}` : base;
  });

  professionalTypeLabel = signal<string>('');
  bodyShopSubTypeLabel = signal<string>('');
  
  // Computed: Display name for professional
  displayName = computed(() => {
    const professional = this.professionalData();
    if (!professional) return '';

    const fullName = `${professional.firstname || ''} ${professional.lastname || ''}`.trim();
    return professional.businessName?.trim() || fullName || '';
  });

  // Registration step history
  stepHistory = signal<RegistrationStepResponse[]>([]);

  // Loading states
  isSubmitting = signal(false);

  // Review form
  reviewNotes = signal('');
  verificationDate = signal<Date | null>(null);
  cribisLink ='https://www2.cribisx.com/Account/LogOn?ReturnUrl=%2f#Home/Index';
  wordCheckLink = 'https://login.ciam.refinitiv.com/as/authorization.oauth2?client_id=55199a53d1804dcbb43f65c03b5085f1cf3b1c0b&response_type=code&scope=trapi%20openid%20profile%20email&state=0.9379987622638337&redirect_uri=https%3A%2F%2Fworldcheck.refinitiv.com%2Fauthping';

  // --- Controlli e Revisione (scaffold FE — persistenza BE da collegare) ---
  /** Sezione "Controlli e Revisione" collassabile (come "Dati Integrativi"). */
  controlliCollapsed = signal<boolean>(true);
  /** Lo stato iniziale di apertura della sezione viene impostato una sola volta (al primo load dello step). */
  private controlliCollapseInitialized = false;
  /** Esito del controllo Cribis (null = non valutato). */
  cribisOutcome = signal<CheckOutcome | null>(null);
  /** Esito del controllo World Check (null = non valutato). */
  wcOutcome = signal<CheckOutcome | null>(null);
  /** Opzioni dell'esito, nell'ordine di navigazione del radiogroup. */
  readonly outcomeOptions: { value: CheckOutcome; label: string; icon: string }[] = [
    { value: 'OK', label: 'OK', icon: 'pi-check-circle' },
    { value: 'KO', label: 'KO', icon: 'pi-times-circle' },
    { value: 'IN_PROGRESS', label: 'Approfondimenti', icon: 'pi-clock' },
  ];
  /** Note interne del controllo: sempre visibili/modificabili, distinte dal messaggio al fiduciario (reviewNotes). */
  internalNotes = signal<string>('');
  /** Riepilogo ultimo salvataggio controlli. */
  controlsSavedMeta = signal<string>('');
  /** True durante il salvataggio dei controlli (PUT). */
  isSavingControls = signal<boolean>(false);
  /** Allegati dei controlli (scaffold FE: in memoria; upload BE da collegare). */
  controlDocuments = signal<ReviewControlDocument[]>([]);
  /** Allegati mappati per il componente riutilizzabile documents-manager. */
  controlDocItems = computed<DocManagerItem[]>(() =>
    this.controlDocuments().map(d => ({
      id: d.id,
      fileName: d.fileName ?? '',
      title: d.title,
      uploadDate: d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString('it-IT') : null,
      fileSize: d.fileSize,
      raw: d,
    }))
  );
  /** True durante l'aggiunta/validazione di un allegato. */
  isAddingDocument = signal<boolean>(false);
  /** Dialog per il messaggio al fiduciario (Richiedi Modifica / Rifiuta). */
  showMessageDialog = signal<boolean>(false);
  /** Azione in corso nel dialog messaggio. */
  messageDialogAction = signal<ReviewType | null>(null);

  // Active tab index (0 = Revisione Attuale, 1 = Storico)
  activeTabIndex = signal(0);

  // Network type flag — computed after professionalData loads
  isNetworkType = computed(() => this.professionalData()?.type === 'NETWORK');

  // The integrative-data container is available for every resolved professional type.
  // The child component still selects the full AXA form or the SOL-only fallback.
  isAxaDataApplicable = computed(() =>
    !!this.professionalData()?.type
  );

  /**
   * Accordion "Gestisci Codici SOL": applicabile ai tipi fiduciario in scope
   * (Perito Auto/RE, Investigatore, Medico, Legale) e non alle sedi operative figlie.
   */
  isSolApplicable = computed(() => {
    const type = this.professionalData()?.type;
    return (
      !this.parentProfessionalId() &&
      !!type &&
      ['AUTO_EXPERT', 'RE_EXPERT', 'INVESTIGATOR', 'DOCTOR', 'LAWYER'].includes(type)
    );
  });

  /**
   * La fase Codici SOL si apre solo dopo l'approvazione dell'anagrafica: il BE crea lo step
   * MFA_SETUP subito dopo la VERIFICATION. Prima (PORTAL_ACCESS/DATA_INPUT/VERIFICATION) e
   * finché lo step non è caricato, l'accordion "Gestisci Codici SOL" resta nascosto.
   */
  isSolPhaseReached = computed(() => {
    const step = this.currentStep();
    return !!step && !['PORTAL_ACCESS', 'DATA_INPUT', 'VERIFICATION'].includes(step);
  });

  /** Collassa la sezione Dati Integrativi quando i dati sono già stati salvati a BE */
  axaDataCollapsed = signal<boolean>(false);

  // AXA user flag (mock — to be replaced with real auth)
  isAxaUser = signal<boolean>(true);

  // Network body shop flag: professionals with NET- prefix belong to a network
  isNetworkBodyShop = computed(() => this.professionalId().startsWith('NET-'));

  // NK_BODYSHOP flag: body shops with NK- prefix (belonging to a network, with dedicated onboarding pages)
  isNkBodyShop = computed(() => this.professionalId().startsWith('NK-'));
  canManageNetworkRelationship = computed(() =>
    this.isAxaUser() && (
      this.isNetworkBodyShop()
      || this.isNkBodyShop()
      || (!!this.networkInfo() && this.professionalData()?.type === 'BODYSHOP')
    )
  );
  networkInfo = signal<{ networkId: string; networkName: string; identificationCode?: string; address?: string } | null>(null);

  // Status helpers for network body shops
  isBodyShopActive = computed(() =>
    this.canManageNetworkRelationship() && this.professionalData()?.status === 'ACTIVE'
  );
  isBodyShopSuspended = computed(() =>
    this.canManageNetworkRelationship() && this.professionalData()?.status === 'SUSPENDED'
  );
  canActivateNetworkBodyShop = computed(() =>
    this.canManageNetworkRelationship()
    && !this.isBodyShopActive()
    && !this.isBodyShopSuspended()
  );

  // Loading state for suspend/reactivate operations
  isSuspendingOrReactivating = signal<boolean>(false);

  // Network body shops data
  networkBodyShopsData = signal<NetworkBodyShop[]>([]);
  isLoadingNetworkBodyShops = signal<boolean>(false);

  // Add body shop dialog
  showAddBodyShopDialog = signal<boolean>(false);
  newBodyShopName = signal<string>('');
  newBodyShopVat = signal<string>('');
  newBodyShopIsControllante = signal<boolean>(false);
  isSavingBodyShop = signal<boolean>(false);

  // Operational location flag: BODYSHOP type (not a network body shop) can have operational locations
  isBodyShopType = computed(() => this.professionalData()?.type === 'BODYSHOP');
  // Operational locations for this body shop
  operationalLocationsData = signal<any[]>([]);
  isLoadingOperationalLocations = signal<boolean>(false);
  // Add operational location dialog
  showAddOperationalLocationDialog = signal<boolean>(false);
  newOpLocationEmail = signal<string>('');
  isSavingOperationalLocation = signal<boolean>(false);

  operationalLocationsColumns: ColumnConfig[] = [
    { title: 'ID', field: 'id', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca ID' },
    { title: 'Codice SOL', field: 'solCode', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca codice SOL' },
    { title: 'Nominativo', field: 'nominativo', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nominativo' },
    { title: 'Indirizzo', field: 'indirizzoCompatto', sortable: false, filterType: 'none' },
    { title: 'Stato', field: 'professionalStatus', type: 'tag', sortable: true, filterType: 'select', filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'In lavorazione', value: 'IN_PROGRESS' },
        { label: 'In attesa', value: 'AWAITING_RESPONSE' },
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'Sospeso', value: 'SUSPENDED' },
        { label: 'Cessato', value: 'CEASED' },
      ]
    },
    { title: 'Dettaglio', type: 'action', filterType: 'none' },
  ];

  networkBodyShopsColumns: ColumnConfig[] = [
    {
      title: 'ID',
      field: 'id',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca ID',
    },
    {
      title: 'Codice SOL',
      field: 'solCode',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca codice SOL',
    },
    {
      title: 'Nominativo',
      field: 'nominativo',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca nominativo',
    },
    {
      title: 'Indirizzo',
      field: 'indirizzoCompatto',
      sortable: false,
      filterType: 'none',
    },
    {
      title: 'Data aggiunta',
      field: 'addedToNetworkAt',
      sortable: true,
      filterType: 'date',
      filterPlaceholder: 'Inserimento data',
    },
    {
      title: 'Data cessazione',
      field: 'cessationDate',
      sortable: true,
      filterType: 'date',
      filterPlaceholder: 'Inserimento data',
    },
    {
      title: 'Stato',
      field: 'status',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterPlaceholder: 'Filtra stato',
      filterOptions: [
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'In lavorazione', value: 'IN_PROGRESS' },
        { label: 'Sospeso', value: 'SUSPENDED' },
        { label: 'Cessato', value: 'CEASED' },
      ],
    },
    {
      title: 'Dettaglio',
      type: 'action',
      filterType: 'none',
    },
  ];

  // Contracts data (only for detail mode)
  contractsData = signal<any[]>([]);
  isLoadingContracts = signal<boolean>(false);
  // Stesse colonne del Registro Contratti fiduciari (contracts-registry -> professionalColumns)
  contractsColumns: ColumnConfig[] = [
    {
      field: 'contractId',
      title: 'ID',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca ID',
    },
    {
      field: 'professionalBusinessName',
      title: 'Ragione Sociale/Nominativo',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca Nome/Ragione Sociale',
    },
    {
      field: 'professionalVatNumber',
      title: 'Partita IVA',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca Partita IVA',
    },
    {
      field: 'professionalCategory',
      title: 'Categoria Fiduciario',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterPlaceholder: 'Filtra categoria',
      filterOptions: [],
    },
    {
      field: 'status',
      title: 'Stato',
      type: 'tag',
      sortable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Attivo', value: 'ACTIVE' },
        { label: 'In Rinnovo', value: 'IN_RENEWAL' },
        { label: 'Cessato', value: 'CEASED' },
      ],
    },
    {
      field: 'startDate',
      title: 'Data di inizio',
      type: 'text',
      sortable: true,
      filterType: 'date',
    },
    {
      field: 'endDate',
      title: 'Data di fine',
      type: 'text',
      sortable: true,
      filterType: 'date',
    },
    {
      field: 'buyer',
      title: 'Buyer',
      type: 'text',
      sortable: true,
      filterType: 'text',
      filterPlaceholder: 'Cerca Buyer',
    },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];

  ngOnInit() {
    this.titleService.setTitle('Revisione Fiduciario - PalEasy');
    // Pre-carica le categorie fiduciario per decodifica sincrona
    this.formDropdownService.preloadTrusteeCategories().subscribe(() => {
      // Popola le opzioni di filtro della colonna "Categoria Fiduciario" della tabella contratti
      this.formDropdownService
        .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
        .subscribe((response) => {
          if (response?.data) {
            const categoryOptions = response.data.map((item: any) => ({
              label: item.valueDescription,
              value: item.valueKey,
            }));
            const categoryColumn = this.contractsColumns.find(
              (col) => col.field === 'professionalCategory',
            );
            if (categoryColumn) {
              categoryColumn.filterOptions = categoryOptions;
            }
          }
        });
    });
    
    // Sottoscrivi i cambiamenti dei parametri della rotta
    // Questo permette di ricaricare i dati quando l'ID cambia (navigazione interna)
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (!id) {
        console.error('❌ No professional ID in route');
        this.router.navigate(['/axa/dashboard']);
        return;
      }

      this.professionalId.set(id);

      // Load professional data
      this.loadProfessionalData(id);

      // Network body shops (NET- prefix) don't have a registration workflow or contracts
      if (id.startsWith('NET-')) {
        this.professionalsService.getNetworkBodyShopById(id).subscribe({
          next: (resp) => {
            const bs = resp?.data;
            if (bs?.networkId) {
              this.networkInfo.set({ networkId: bs.networkId, networkName: bs.networkName ?? '' });
            }
          },
          error: () => this.networkInfo.set(null),
        });
        return;
      }

      // NK_BODYSHOP: load networkId from body-shop detail (not in summary response)
      if (id.startsWith('NK-')) {
        this.professionalsService.getBodyshopData(id).subscribe({
          next: (resp) => {
            const bs = resp?.data;
            const networkId = bs?.networkId as string | undefined;
            if (networkId) {
              forkJoin({
                network: this.networkService.getNetwork(networkId),
                generalData: this.professionalsService.getProfessionalGeneralData(networkId),
                streetTypes: this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME'),
              }).subscribe({
                next: ({ network, generalData, streetTypes }: any) => {
                  const netData = network?.data;
                  const gd = generalData?.data;
                  const cd = gd?.companyData;
                  const identificationCode = cd?.vatNumber || cd?.fiscalCode || gd?.vatNumber || gd?.fiscalCode || '';
                  const addr = gd?.legalAddress ?? null;
                  const streetTypeMap = new Map<string, string>(
                    (streetTypes?.data ?? []).map((item: any) => [item.valueKey, item.valueDescription])
                  );
                  this.formatCompactAddress(addr, streetTypeMap).subscribe((address) => {
                    this.networkInfo.set({
                      networkId: netData?.id || networkId,
                      networkName: netData?.networkName || netData?.name || networkId,
                      identificationCode: identificationCode || undefined,
                      address: address || undefined,
                    });
                  });
                },
                error: () => {},
              });
            }
          },
          error: () => {},
        });
      }

      // Load current step to determine mode (review vs detail)
      this.loadCurrentStep(id);

      // Load registration step history
      this.loadStepHistory(id);

      // Load contracts (will be shown only in detail mode)
      this.loadContracts(id);

      // Load "Controlli e Revisione" (note, data verifica, esiti, allegati)
      this.loadReviewControl(id);
    });
  }

  ngAfterViewInit(): void {
    this.enableNestedTextSelection();

    this.selectionObserver = new MutationObserver(() => {
      this.enableNestedTextSelection();
    });

    this.selectionObserver.observe(this.hostElement.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'class', 'hidden'],
    });
  }

  ngOnDestroy(): void {
    this.selectionObserver?.disconnect();
    this.selectionObserver = null;
  }

  private enableNestedTextSelection(): void {
    const root = this.hostElement.nativeElement;
    const selectors = '.read-only-mode input[disabled], .read-only-mode textarea[disabled]';
    const elements = Array.from(root.querySelectorAll(selectors)) as Array<
      HTMLInputElement | HTMLTextAreaElement
    >;

    elements.forEach((element: HTMLInputElement | HTMLTextAreaElement) => {
      if (element instanceof HTMLInputElement) {
        const inputType = (element.type || 'text').toLowerCase();
        if (!this.textSelectableInputTypes.has(inputType)) {
          return;
        }
      }

      element.disabled = false;
      element.readOnly = true;
      element.setAttribute('aria-readonly', 'true');
      element.classList.add('review-text-selectable');
    });
  }

  /**
   * Load current registration step
   * Determines if page is in review mode (VERIFICATION step) or detail mode (other steps)
   */
  private loadCurrentStep(professionalId: string): void {
    this.registrationStepService.getCurrentStep(professionalId).subscribe({
      next: (response) => {
        if (response?.step) {
          this.currentStep.set(response.step);
          this.currentStepStatus.set(response.status || '');
          // Flag persistito: la compilazione MFA è stata invalidata da una modifica del BO.
          this.mfaReviewDirty.set(!!response.mfaReviewDirty);
          console.log('📍 Current step:', response.step, '| Mode:', this.isReviewMode() ? 'REVIEW' : 'DETAIL');
          // All'ingresso in pagina: se in review mode (VERIFICATION) la sezione Controlli e Revisione
          // parte aperta; negli altri step resta chiusa. Solo al primo load (non sovrascrive i toggle manuali).
          if (!this.controlliCollapseInitialized) {
            this.controlliCollapseInitialized = true;
            this.controlliCollapsed.set(!this.isReviewMode());
          }
          // In fase MFA carica i dati (mail principale/responsabile/associazioni) per
          // disambiguare richiesta vs revisione e per la consultazione del Back Office.
          if (response.step === 'MFA_SETUP') {
            this.loadMfaData(professionalId);
          } else {
            this.mfaData.set([]);
          }

          // Post-approvazione: entrati nella fase SOL, forziamo il refresh dei codici SOL dal BE
          // (primo impianto = codici già presenti). Rimandiamo di un tick così l'@if ha montato
          // il componente e la @ViewChild è valorizzata.
          if (this.reloadSolAfterStep && this.isSolApplicable() && this.isSolPhaseReached()) {
            this.reloadSolAfterStep = false;
            setTimeout(() => this.solCodesManagerComp?.reload());
          }
        } else {
          this.currentStep.set('');
          this.currentStepStatus.set('');
          this.mfaReviewDirty.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading current step:', error);
        this.currentStep.set('');
        this.currentStepStatus.set('');
      }
    });
  }

  /**
   * Carica i dati MFA (codici SOL con mail principale, responsabile e associazioni)
   * per la fase MFA_SETUP. Usati sia per determinare la sotto-fase (richiesta/revisione)
   * sia per la consultazione read-only del Back Office.
   */
  private loadMfaData(professionalId: string): void {
    this.isLoadingMfa.set(true);
    this.solCodeService.getMfaData(professionalId).subscribe({
      next: (resp) => {
        this.mfaData.set(resp?.data ?? []);
        this.isLoadingMfa.set(false);
      },
      error: (error) => {
        console.error('Error loading MFA data:', error);
        this.mfaData.set([]);
        this.isLoadingMfa.set(false);
      },
    });
  }

  /**
   * In validazione MFA il BO ha modificato i codici SOL (crea/modifica/elimina): la
   * compilazione MFA non è più valida. Disabilita subito l'approvazione (optimistic) e
   * ricarica lo step corrente per allineare il flag persistito e i dati MFA/associazioni.
   */
  onMfaSolCodesChanged(): void {
    this.mfaReviewDirty.set(true);
    const professionalId = this.professionalId();
    if (professionalId) {
      this.loadCurrentStep(professionalId);
    }
  }

  /**
   * Load professional data by ID
   * Uses getProfessionals() with ID filter to get summary data
   */
  /**
   * Formatta un indirizzo compatto per la sola visualizzazione, decodificando il
   * tipo via (mappa PLACE_NAME) e il nome del comune a partire dal codice salvato.
   * Restituisce un Observable perché la decodifica del comune richiede l'anagrafica
   * (caricata per provincia e messa in cache dal GeographyService); i dati legacy
   * salvati come nome vengono comunque mostrati grazie al fallback in resolveMunicipalityName.
   */
  private formatCompactAddress(addr: any, streetTypeMap: Map<string, string>): Observable<string> {
    if (!addr) return of('');
    return this.geographyService
      .resolveMunicipalityName(addr.municipality, addr.province)
      .pipe(
        map((municipality) =>
          [
            [streetTypeMap.get(addr.streetType) || addr.streetType, addr.streetName, addr.streetNumber]
              .filter(Boolean)
              .join(' '),
            [municipality, addr.province ? `(${addr.province})` : null].filter(Boolean).join(' '),
            addr.zipCode,
          ]
            .filter(Boolean)
            .join(', '),
        ),
      );
  }

  private loadProfessionalData(professionalId: string): void {
    this.isLoadingProfessional.set(true);
    
    // Use getProfessionals with ID filter (API v0.0.4)
    this.professionalsService.getProfessionals(0, 1, undefined, { id: professionalId }).subscribe({
      next: (response) => {
        if (response?.status && response?.data?.content && response.data.content.length > 0) {
          const summaryData = response.data.content[0];
          this.parentProfessionalId.set(response.data.content[0].parentProfessionalId || null);
          const parentId = response.data.content[0].parentProfessionalId || null;
          if (parentId) {
            forkJoin({
              generalData: this.professionalsService.getProfessionalGeneralData(parentId),
              streetTypes: this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME'),
            }).subscribe({
              next: ({ generalData, streetTypes }: any) => {
                const d = generalData?.data;
                if (!d) { this.parentInfo.set(null); return; }
                const cd = d.companyData;
                const businessName = cd?.businessName || d.businessName
                  || [cd?.firstname || d.firstname, cd?.lastname || d.lastname].filter(Boolean).join(' ')
                  || '';
                const identificationCode = cd?.vatNumber || cd?.fiscalCode || d.vatNumber || d.fiscalCode || '';
                const addr = d.legalAddress ?? null;
                const streetTypeMap = new Map<string, string>(
                  (streetTypes?.data ?? []).map((item: any) => [item.valueKey, item.valueDescription])
                );
                this.formatCompactAddress(addr, streetTypeMap).subscribe((addrStr) => {
                  this.parentInfo.set({ businessName, identificationCode, address: addrStr });
                });
              },
              error: () => this.parentInfo.set(null),
            });
          }
          // Map ProfessionalSummaryResponse to ProfessionalRegistryItem (partial)
          const resolvedName = summaryData.businessName
            || [summaryData.firstName, summaryData.lastName].filter(Boolean).join(' ')
            || '';
          const professionalData: Partial<ProfessionalRegistryItem> = {
            id: summaryData.id,
            businessName: resolvedName,
            type: summaryData.type,
            legalOrPhysical: summaryData.legalOrPhysical,
            // Extract firstname/lastname from resolved name for PHYSICAL persons
            firstname: summaryData.legalOrPhysical === 'PHYSICAL' ? (summaryData.firstName || resolvedName.split(' ')[0]) : '',
            lastname: summaryData.legalOrPhysical === 'PHYSICAL' ? (summaryData.lastName || resolvedName.split(' ').slice(1).join(' ')) : '',
            status: summaryData.professionalStatus,
            createdBy: summaryData.createdBy ?? null,
          };
          
          this.professionalData.set(professionalData);
          this.loadReviewAlerts(summaryData.id);

          if (professionalData.type === 'NETWORK') {
            this.loadNetworkBodyShops(summaryData.id);
            this.operationalLocationsData.set([]);
          } else if (professionalData.type === 'BODYSHOP' && !parentId) {
            this.loadOperationalLocations(summaryData.id);
            this.networkBodyShopsData.set([]);
          } else {
            this.networkBodyShopsData.set([]);
            this.operationalLocationsData.set([]);
          }

          // Load decoded type label from TRUSTEE_CATEGORY dropdown
          this.formDropdownService.getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY').subscribe({
            next: (resp: any) => {
              const items: any[] = resp?.data ?? [];
              const match = items.find(i => i.valueKey === summaryData.type);
              this.professionalTypeLabel.set(match?.valueDescription || '');
            },
            error: () => {},
          });

          // For BODYSHOP type, also load sub-type from specific data
          if (professionalData.type === 'BODYSHOP') {
            const bodyShopTypeMap: Record<string, string> = {
              STANDARD: 'Carrozzeria Standard',
              ARS: 'AXA Repair Services',
              RCI: 'Rete Carrozzerie Italiane',
              NOBIS: 'Carrozzeria NOBIS',
              AXA_DEUTSCHE_BANK: 'Deutsche Bank',
            };
            this.professionalsService.getProfessionalSpecificData(summaryData.id).subscribe({
              next: (resp: any) => {
                const bt = resp?.data?.bodyShopType || resp?.data?.specificData?.bodyShopType || '';
                this.bodyShopSubTypeLabel.set(bodyShopTypeMap[bt] || bt || '');
              },
              error: () => {},
            });
          }

          // If the summary response carries a networkId (e.g. BODYSHOP belonging to a network),
          // load the network details and populate networkInfo so the network banner is shown.
          const networkId = summaryData.networkId;
          if (networkId && !this.networkInfo()) {
            this.networkService.getNetwork(networkId).subscribe({
              next: (resp) => {
                if (resp?.data) {
                  this.networkInfo.set({ networkId: resp.data.id, networkName: resp.data.networkName || networkId });
                }
              },
              error: () => {},
            });
          }
        } else {
          this.professionalData.set(null);
          this.reviewAlerts.set([]);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Dati del fiduciario non trovati.',
            life: 4000,
          });
        }
        this.isLoadingProfessional.set(false);
      },
      error: (error) => {
        console.error('Error loading professional data:', error);
        this.professionalData.set(null);
        this.reviewAlerts.set([]);
        this.isLoadingProfessional.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i dati del fiduciario.',
          life: 5000,
        });
      },
    });
  }

  private loadReviewAlerts(professionalId: string): void {
    this.professionalsService.getAlerts(true).subscribe({
      next: (alerts) => {
        this.reviewAlerts.set(
          (alerts ?? []).filter((alert) => alert.professionalId === professionalId && !alert.resolved),
        );
      },
      error: (error) => {
        console.error('Error loading review alerts:', error);
        this.reviewAlerts.set([]);
      },
    });
  }

  /**
   * Load registration step history for the professional
   */
  private loadStepHistory(professionalId: string): void {
    this.registrationStepService.getStepHistory(professionalId).subscribe({
      next: (response) => {
        if (response && Array.isArray(response)) {
          // Sort by date descending (most recent first)
          const sorted = [...response].sort((a, b) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          this.stepHistory.set(sorted);
        } else {
          this.stepHistory.set([]);
        }
      },
      error: (error) => {
        console.error('Error loading registration step history:', error);
        this.stepHistory.set([]);
      }
    });
  }

  /**
   * Load contracts for the professional (only shown in detail mode)
   */
  private loadContracts(professionalId: string): void {
    this.isLoadingContracts.set(true);
    
    this.contractService.getContractsByProfessional(professionalId).subscribe({
      next: (response) => {
        // La lista contratti arriva paginata (response.data.content); fallback ad array diretto per robustezza.
        const raw = Array.isArray(response?.data?.content)
          ? response.data.content
          : Array.isArray(response?.data)
            ? response.data
            : [];
        if (response?.status && raw.length) {
          // Appiattisce i campi (contractId/serviceType/contractObject/date/status) come nel registro contratti.
          this.contractsData.set(this.contractService.mapContractsForDisplay(raw));
        } else {
          this.contractsData.set([]);
        }
        this.isLoadingContracts.set(false);
        // Dalla fase contrattuale in poi carica il documento del contratto (dettaglio):
        // serve sia a differenziare il banner controfirma (carica vs attiva) sia ai download nello storico.
        this.loadContractMainDoc();
      },
      error: (error) => {
        console.error('Error loading contracts:', error);
        this.contractsData.set([]);
        this.isLoadingContracts.set(false);
      }
    });
  }

  /**
   * Dalla fase contrattuale in poi legge il dettaglio del contratto di riferimento per ottenere il
   * documento principale (mainDocument). Serve a: differenziare il banner controfirma (carica vs attiva)
   * e mostrare i download dei documenti (firmato/controfirmato) nello storico registrazione.
   */
  private loadContractMainDoc(): void {
    const contractId = this.primaryContract()?.contractId;
    if (!this.isContractStepOrLater() || !contractId) {
      this.contractMainDoc.set(null);
      return;
    }
    this.contractService.getContractById(contractId).subscribe({
      next: (response) => this.contractMainDoc.set(response?.data?.mainDocument ?? null),
      error: () => this.contractMainDoc.set(null),
    });
  }

  /**
   * Scarica un documento del contratto di riferimento dallo storico.
   * kind 'signed' = firmato dal fiduciario (EXTERNALLY_SIGNED), 'countersigned' = controfirmato AXA (FINAL).
   */
  downloadHistoryContractDocument(kind: 'signed' | 'countersigned'): void {
    const contractId = this.primaryContract()?.contractId;
    if (!contractId) {
      return;
    }
    const documentType = kind === 'signed' ? DocumentType.EXTERNALLY_SIGNED : DocumentType.FINAL;
    const fileName = kind === 'signed' ? 'contratto-firmato.pdf' : 'contratto-controfirmato.pdf';
    const label = kind === 'signed' ? 'Contratto firmato' : 'Contratto controfirmato';

    this.contractService.downloadMainDocument(contractId, documentType).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.messageService.add({
          severity: 'success',
          summary: 'Download completato',
          detail: `${label} scaricato con successo`,
          life: 3000,
        });
      },
      error: (error: any) => {
        console.error(`Error downloading ${documentType} document:`, error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || `Errore durante il download del documento`,
          life: 5000,
        });
      },
    });
  }

  /**
   * Tipo di documento scaricabile associato a una voce di storico (null se nessuno).
   * CONTRACT_SIGNATURE completato → contratto firmato; CONTRACT_COUNTERSIGNATURE completato → controfirmato.
   */
  historyContractDocument(
    step: string | null | undefined,
    status: string | null | undefined,
  ): 'signed' | 'countersigned' | null {
    if ((status || '').toUpperCase() !== 'COMPLETED') {
      return null;
    }
    switch ((step || '').toUpperCase()) {
      case 'CONTRACT_SIGNATURE':
        return this.hasSignedContractDoc() ? 'signed' : null;
      case 'CONTRACT_COUNTERSIGNATURE':
        return this.hasCountersignedContractDoc() ? 'countersigned' : null;
      default:
        return null;
    }
  }

  /**
   * Load carrozzerie (body shops) belonging to this network
   * Only relevant when professionalData().type === 'NETWORK'
   * Enriches each item with computed fields: nominativo, indirizzoCompatto
   */
  private loadNetworkBodyShops(professionalId: string): void {
    this.isLoadingNetworkBodyShops.set(true);
    forkJoin({
      bodyShops: this.networkService.getNetworkBodyShops(professionalId),
      streetTypes: this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME'),
    }).subscribe({
      next: ({ bodyShops, streetTypes }: any) => {
        if (bodyShops?.status && bodyShops?.data) {
          const streetTypeMap = new Map<string, string>(
            (streetTypes?.data ?? []).map((item: any) => [item.valueKey, item.valueDescription])
          );
          const items: any[] = Array.isArray(bodyShops.data) ? bodyShops.data : [];
          const enriched$ = items.map((bs: any) => {
            const nominativo = bs.businessName
              || [bs.firstname, bs.lastname].filter(Boolean).join(' ')
              || bs.id;
            const addr = bs.facilityAddress ?? bs.legalAddress ?? null;
            return this.formatCompactAddress(addr, streetTypeMap).pipe(
              map((indirizzoCompatto) => ({ ...bs, nominativo, indirizzoCompatto })),
            );
          });
          (enriched$.length ? forkJoin(enriched$) : of([])).subscribe((enriched) => {
            this.networkBodyShopsData.set(enriched);
          });
        } else {
          this.networkBodyShopsData.set([]);
        }
        this.isLoadingNetworkBodyShops.set(false);
      },
      error: (error) => {
        console.error('Error loading network body shops:', error);
        this.networkBodyShopsData.set([]);
        this.isLoadingNetworkBodyShops.set(false);
      },
    });
  }

  /**
   * Load operational locations (sedi operative) for this body shop
   * Only relevant when professionalData().type === 'BODYSHOP'
   */
  private loadOperationalLocations(professionalId: string): void {
    this.isLoadingOperationalLocations.set(true);
    forkJoin({
      locations: this.professionalsService.getOperationalLocations(professionalId),
      streetTypes: this.formDropdownService.getFormDropdownValues('PLACE_NAME', 'PLACE_NAME'),
    }).subscribe({
      next: ({ locations, streetTypes }: any) => {
        if (locations?.status && locations?.data) {
          const streetTypeMap = new Map<string, string>(
            (streetTypes?.data ?? []).map((item: any) => [item.valueKey, item.valueDescription])
          );
          const items: any[] = Array.isArray(locations.data) ? locations.data : [];
          const enriched$ = items.map((loc: any) => {
            const nominativo = loc.businessName
              || [loc.firstname, loc.lastname].filter(Boolean).join(' ')
              || loc.registrationEmail
              || loc.id;
            const addr = loc.specificData?.operationalAddress ?? loc.legalAddress ?? null;
            // Compute professionalStatus mirroring /professionals API logic
            const professionalStatus = loc.professionalStatus
              || (loc.businessStatus === 'SUSPENDED' ? 'SUSPENDED'
                : loc.businessStatus === 'CEASED' ? 'CEASED'
                : loc.currentStepKey === 'ACTIVE' ? 'ACTIVE'
                : loc.status ?? 'IN_PROGRESS');
            return this.formatCompactAddress(addr, streetTypeMap).pipe(
              map((indirizzoCompatto) => ({ ...loc, nominativo, indirizzoCompatto, professionalStatus })),
            );
          });
          (enriched$.length ? forkJoin(enriched$) : of([])).subscribe((enriched) => {
            this.operationalLocationsData.set(enriched);
          });
        } else {
          this.operationalLocationsData.set([]);
        }
        this.isLoadingOperationalLocations.set(false);
      },
      error: (error: any) => {
        console.error('Error loading operational locations:', error);
        this.operationalLocationsData.set([]);
        this.isLoadingOperationalLocations.set(false);
      },
    });
  }

  onAddOperationalLocation(): void {
    this.router.navigate(['/axa/pre-registration'], {
      queryParams: {
        mode: 'DELEGATED',
        parentId: this.professionalId(),
        returnTo: encodeURIComponent(`/axa/review-professional/${this.professionalId()}`),
      },
    });
  }

  onConfirmAddOperationalLocation(): void {
    const email = this.newOpLocationEmail().trim();
    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Campo obbligatorio',
        detail: 'Inserisci l\'email di registrazione per la sede operativa.',
        life: 4000,
      });
      return;
    }
    this.isSavingOperationalLocation.set(true);
    this.professionalsService.createOperationalLocation(this.professionalId(), { registrationEmail: email }).subscribe({
      next: (res) => {
        this.isSavingOperationalLocation.set(false);
        if (res.status) {
          this.showAddOperationalLocationDialog.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Sede operativa creata',
            detail: `La sede operativa è stata creata. Un\'email di invito è stata inviata a ${email}.`,
            life: 5000,
          });
          this.loadOperationalLocations(this.professionalId());
        }
      },
      error: () => {
        this.isSavingOperationalLocation.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile creare la sede operativa.',
          life: 5000,
        });
      },
    });
  }

  onOperationalLocationDetail(location: any): void {
    this.router.navigate(['/axa/review-professional', location.id]);
  }

  /**
   * Button "Aggiungi carrozzeria nel network" — only for AXA users
   */
  onAddBodyShopToNetwork(): void {
    this.newBodyShopName.set('');
    this.newBodyShopVat.set('');
    this.newBodyShopIsControllante.set(false);
    this.showAddBodyShopDialog.set(true);
  }

  /**
   * Confirm creation of new body shop in the network
   */
  onConfirmAddBodyShop(): void {
    const name = this.newBodyShopName().trim();
    if (!name) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Campo obbligatorio',
        detail: 'Inserisci la ragione sociale della carrozzeria.',
        life: 4000,
      });
      return;
    }
    this.isSavingBodyShop.set(true);
    this.networkService.addBodyShopToNetwork(this.professionalId(), {
      businessName: name,
      vatNumber: this.newBodyShopVat().trim() || undefined,
      controllante: this.newBodyShopIsControllante(),
    }).subscribe({
      next: (res) => {
        this.isSavingBodyShop.set(false);
        if (res.status) {
          this.showAddBodyShopDialog.set(false);
          const newId = res.data?.id;
          if (newId) {
            this.router.navigate(['/axa/professionals-specific-data', newId]);
          } else {
            this.messageService.add({
              severity: 'success',
              summary: 'Carrozzeria aggiunta',
              detail: `${name} è stata aggiunta al network con successo.`,
              life: 4000,
            });
            this.loadNetworkBodyShops(this.professionalId());
          }
        }
      },
      error: () => {
        this.isSavingBodyShop.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile aggiungere la carrozzeria al network.',
          life: 5000,
        });
      },
    });
  }

  /**
   * Navigate to body shop detail page
   */
  onNetworkBodyShopDetail(bodyShop: NetworkBodyShop): void {
    this.router.navigate(['/axa/review-professional', bodyShop.id]);
  }

  /**
   * Remove body shop from network (AXA only)
   */
  onNetworkBodyShopDelete(bodyShop: NetworkBodyShop): void {
    this.confirmationService.confirm({
      message: `Vuoi rimuovere la carrozzeria <strong>${bodyShop.businessName}</strong> dal network?`,
      header: 'Conferma Rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Rimuovi',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.networkService.removeBodyShopFromNetwork(bodyShop.id).subscribe({
          next: (res) => {
            if (res.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Carrozzeria rimossa',
                detail: `${bodyShop.businessName} è stata rimossa dal network.`,
                life: 4000,
              });
              this.loadNetworkBodyShops(this.professionalId());
            }
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile rimuovere la carrozzeria dal network.',
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Suspend a network body shop (AXA only)
   */
  onSuspendBodyShop(): void {
    this.confirmationService.confirm({
      message: `Vuoi sospendere la carrozzeria <strong>${this.displayName()}</strong>?`,
      header: 'Conferma Sospensione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sospendi',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.isSuspendingOrReactivating.set(true);
        this.networkService.updateBodyShopStatus(this.professionalId(), 'SUSPEND').subscribe({
          next: (res) => {
            if (res.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Carrozzeria sospesa',
                detail: `${this.displayName()} è stata sospesa.`,
                life: 4000,
              });
              this.loadProfessionalData(this.professionalId());
            }
            this.isSuspendingOrReactivating.set(false);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile sospendere la carrozzeria.',
              life: 5000,
            });
            this.isSuspendingOrReactivating.set(false);
          },
        });
      },
    });
  }

  /**
   * Activate a network body shop (or reactivate if already suspended)
   */
  onActivateBodyShop(): void {
    const wasSuspended = this.professionalData()?.status === 'SUSPENDED';
    this.confirmationService.confirm({
      message: `Vuoi ${wasSuspended ? 'riattivare' : 'attivare'} la carrozzeria <strong>${this.displayName()}</strong>?`,
      header: wasSuspended ? 'Conferma Riattivazione' : 'Conferma Attivazione',
      icon: 'pi pi-info-circle',
      acceptLabel: wasSuspended ? 'Riattiva' : 'Attiva',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.isSuspendingOrReactivating.set(true);
        this.networkService.updateBodyShopStatus(this.professionalId(), 'REACTIVATE').subscribe({
          next: (res) => {
            if (res.status) {
              this.messageService.add({
                severity: 'success',
                summary: wasSuspended ? 'Carrozzeria riattivata' : 'Carrozzeria attivata',
                detail: `${this.displayName()} è stata ${wasSuspended ? 'riattivata' : 'attivata'}.`,
                life: 4000,
              });
              this.loadProfessionalData(this.professionalId());
            }
            this.isSuspendingOrReactivating.set(false);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: `Impossibile ${wasSuspended ? 'riattivare' : 'attivare'} la carrozzeria.`,
              life: 5000,
            });
            this.isSuspendingOrReactivating.set(false);
          },
        });
      },
    });
  }

  /**
   * Remove the current network body shop from its network (AXA only)
   */
  onDeleteCurrentBodyShop(): void {
    this.confirmationService.confirm({
      message: `Vuoi eliminare la carrozzeria <strong>${this.displayName()}</strong> dal network?`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.isSuspendingOrReactivating.set(true);
        this.networkService.removeBodyShopFromNetwork(this.professionalId()).subscribe({
          next: (res) => {
            this.isSuspendingOrReactivating.set(false);
            if (res.status) {
              const networkId = this.networkInfo()?.networkId;
              this.messageService.add({
                severity: 'success',
                summary: 'Carrozzeria eliminata',
                detail: `${this.displayName()} è stata rimossa dal network.`,
                life: 4000,
              });
              setTimeout(() => {
                if (networkId) {
                  this.router.navigate(['/axa/review-professional', networkId]);
                } else {
                  this.router.navigate(['/axa/dashboard']);
                }
              }, 1200);
            }
          },
          error: () => {
            this.isSuspendingOrReactivating.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare la carrozzeria dal network.',
              life: 5000,
            });
          },
        });
      },
    });
  }

  /**
   * Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Submit review (APPROVE)
   * Blocked if AXA operational data has unfilled required fields
   * (applies to BODYSHOP/NETWORK: authority + altri campi; HAIL_PROVIDER: authority only)
   */
  submitApprove(): void {
    if (this.operationalDetailsComp?.isApplicable() && this.operationalDetailsComp?.hasValidationErrors()) {
      // Mark all fields as touched so inline errors appear in red
      this.operationalDetailsComp.triggerValidation();
      const sections = this.operationalDetailsComp.showOtherFields()
        ? '"Authority" e "Altri Campi"'
        : '"Authority"';
      this.messageService.add({
        severity: 'error',
        summary: 'Campi obbligatori mancanti',
        detail: `Completare i campi obbligatori nelle sezioni ${sections} prima di validare il fiduciario.`,
        life: 6000,
      });
      return;
    }
    // NB: nessun gate sui Codici SOL prima dell'approvazione anagrafica. La raccolta dei
    // dati SOL (mail principale, responsabile, associazioni) avviene nella fase MFA che si
    // apre subito dopo l'approvazione (allineato al backend, che ha rimosso isSectionApprovable).
    // Apre il dialog con messaggio al fiduciario FACOLTATIVO.
    this.openMessageDialog('APPROVE');
  }

  /**
   * Apre il dialog per il messaggio al fiduciario.
   * Il messaggio è obbligatorio per REQUEST_MODIFICATION / REJECT, facoltativo per APPROVE.
   */
  openMessageDialog(action: ReviewType): void {
    this.messageDialogAction.set(action);
    this.reviewNotes.set('');
    this.showMessageDialog.set(true);
  }

  /** Chiude il dialog messaggio e azzera l'azione corrente. */
  closeMessageDialog(): void {
    this.showMessageDialog.set(false);
    this.messageDialogAction.set(null);
  }

  /** Conferma il dialog: valida il messaggio (se richiesto) e invia la revisione. */
  confirmMessageDialog(): void {
    const action = this.messageDialogAction();
    if (!action) return;
    // Messaggio obbligatorio solo per Richiedi Modifica / Rifiuta; facoltativo per Approva.
    if (action !== 'APPROVE' && !this.reviewNotes()?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Messaggio obbligatorio',
        detail: action === 'REJECT'
          ? 'Il messaggio al fiduciario è obbligatorio per rifiutare la domanda'
          : 'Il messaggio al fiduciario è obbligatorio per richiedere una modifica',
      });
      return;
    }
    this.showMessageDialog.set(false);
    this.submitReview(action);
  }

  /**
   * Submit review to backend
   */
  private submitReview(reviewType: ReviewType): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    const request = {
      reviewType,
      notes: this.reviewNotes()?.trim() || undefined,
    };

    // Validate request
    const validationError = this.reviewService.validateReviewRequest(request);
    if (validationError) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore di validazione',
        detail: validationError,
      });
      return;
    }

    this.isSubmitting.set(true);

    this.reviewService.submitReview(professionalId, request).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);
        
        const actionText = reviewType === 'APPROVE' ? 'approvata' 
                         : reviewType === 'REQUEST_MODIFICATION' ? 'modificata' 
                         : 'rifiutata';
        
        this.messageService.add({
          severity: 'success',
          summary: 'Revisione completata',
          detail: `La domanda è stata ${actionText} con successo`,
        });

        // Primo impianto: dopo l'approvazione anagrafica i codici SOL possono già esistere
        // a BE e vanno recuperati. Chiediamo il refresh esplicito una volta entrati in fase SOL.
        this.reloadSolAfterStep = reviewType === 'APPROVE';

        // Reload current step to update UI state
        this.loadCurrentStep(professionalId);

        // Reload registration step history
        this.loadStepHistory(professionalId);

        // Reset form
        this.reviewNotes.set('');
        this.verificationDate.set(null);
      },
      error: (error) => {
        this.isSubmitting.set(false);
        console.error('Error submitting review:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Errore durante l\'invio della revisione',
        });
      }
    });
  }


  /** Esporta in Excel i dati MFA (codici SOL) del fiduciario (modello MFA_01). */
  exportMfaExcel(): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    this.isExportingMfa.set(true);
    this.documentsService.exportRegistry('MFA_01', null, null, null, professionalId).subscribe({
      next: (response) => {
        this.isExportingMfa.set(false);
        const blob = response.body as Blob | null;
        if (!blob) {
          this.messageService.add({ severity: 'error', summary: 'Errore export', detail: 'File non ricevuto dal server', life: 4000 });
          return;
        }
        let fileName = `MFA_${professionalId}.xlsx`;
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) fileName = decodeURIComponent(match[1]);
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.messageService.add({ severity: 'success', summary: 'Export completato', detail: 'Il file MFA è stato scaricato con successo', life: 3000 });
      },
      error: () => {
        this.isExportingMfa.set(false);
        this.messageService.add({ severity: 'error', summary: 'Errore export', detail: 'Impossibile esportare i dati MFA', life: 4000 });
      },
    });
  }

  // ── Azioni fase MFA (Codici SOL) ──────────────────────────────────────────

  /**
   * BO: apre la compilazione dei dati MFA al fiduciario.
   * MFA_SETUP PENDING (dati non compilati) → USER_INPUT + email di notifica (M1).
   */
  /**
   * True se "Richiedi compilazione MFA" va inibito: il fiduciario è di un tipo
   * con codici SOL ma non ha ancora il codice SOL master (unico requisito
   * obbligatorio). Senza master l'API di richiesta compilazione va in errore.
   */
  isRequestMfaBlocked(): boolean {
    return this.isSolApplicable() && !this.solCodesManagerComp?.hasMaster();
  }

  requestMfaCompilation(): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    if (this.isRequestMfaBlocked()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Codice SOL master mancante',
        detail: 'Prima di richiedere la compilazione MFA devi creare il codice SOL master del fiduciario.',
        life: 6000,
      });
      return;
    }

    this.isMfaSubmitting.set(true);
    this.solCodeService.requestMfaCompilation(professionalId).subscribe({
      next: () => {
        this.isMfaSubmitting.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Richiesta inviata',
          detail: 'È stata richiesta al fiduciario la compilazione dei dati MFA (codici SOL). Una email di notifica è stata inviata.',
          life: 5000,
        });
        this.loadCurrentStep(professionalId);
        this.loadStepHistory(professionalId);
      },
      error: (error) => {
        this.isMfaSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Impossibile richiedere la compilazione dei dati MFA.',
          life: 6000,
        });
      },
    });
  }

  /**
   * BO: salta la fase MFA per i primi impianti con SOL già attivi.
   * MFA_SETUP PENDING iniziale → COMPLETED e avanza a CONTRACT_SENT, senza passare dal fiduciario.
   */
  skipMfa(): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    this.confirmationService.confirm({
      header: 'Salta compilazione MFA',
      message:
        'Il fiduciario è di primo impianto con i codici SOL già tutti attivi. Saltando la fase MFA ' +
        'i codici vengono validati d\'ufficio e si procede con l\'invio del contratto, senza chiedere ' +
        'la compilazione al fiduciario. Confermi?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Conferma e procedi',
      rejectLabel: 'Annulla',
      accept: () => {
        this.isMfaSubmitting.set(true);
        this.solCodeService.skipMfa(professionalId).subscribe({
          next: () => {
            this.isMfaSubmitting.set(false);
            this.messageService.add({
              severity: 'success',
              summary: 'Fase MFA saltata',
              detail: 'I codici SOL sono stati validati d\'ufficio. Si procede con l\'invio del contratto.',
              life: 5000,
            });
            this.loadCurrentStep(professionalId);
            this.loadStepHistory(professionalId);
          },
          error: (error) => {
            this.isMfaSubmitting.set(false);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: error.error?.message || 'Impossibile saltare la fase MFA.',
              life: 6000,
            });
          },
        });
      },
    });
  }

  /**
   * BO: valida i dati MFA compilati dal fiduciario.
   * MFA_SETUP PENDING (dati compilati) → COMPLETED e avanza a CONTRACT_SENT.
   */
  approveMfa(): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    this.isMfaSubmitting.set(true);
    this.solCodeService.reviewMfa(professionalId, 'APPROVE').subscribe({
      next: () => {
        this.isMfaSubmitting.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Dati MFA validati',
          detail: 'I dati MFA sono stati approvati. Si procede con l\'invio del contratto.',
          life: 5000,
        });
        this.reviewNotes.set('');
        this.loadCurrentStep(professionalId);
        this.loadStepHistory(professionalId);
      },
      error: (error) => {
        this.isMfaSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Impossibile validare i dati MFA.',
          life: 6000,
        });
      },
    });
  }

  /**
   * BO: richiede al fiduciario la modifica dei dati MFA (nota obbligatoria).
   * MFA_SETUP PENDING → MODIFICATION_REQUESTED e riapre un nuovo MFA_SETUP USER_INPUT.
   * In fase MFA non è previsto il rifiuto.
   */
  requestMfaModification(): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    const notes = this.reviewNotes()?.trim();
    if (!notes) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Le note sono obbligatorie per richiedere una modifica dei dati MFA.',
        life: 4000,
      });
      return;
    }

    this.isMfaSubmitting.set(true);
    this.solCodeService.reviewMfa(professionalId, 'REQUEST_MODIFICATION', notes).subscribe({
      next: () => {
        this.isMfaSubmitting.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Modifica richiesta',
          detail: 'È stata richiesta al fiduciario la modifica dei dati MFA.',
          life: 5000,
        });
        this.reviewNotes.set('');
        this.loadCurrentStep(professionalId);
        this.loadStepHistory(professionalId);
      },
      error: (error) => {
        this.isMfaSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Impossibile richiedere la modifica dei dati MFA.',
          life: 6000,
        });
      },
    });
  }

  /**
   * Open external link
   */
  openLink(url: string): void {
    window.open(url, '_blank');
  }

  // ===========================================================================
  // Controlli e Revisione — esiti Cribis / World Check (scaffold FE)
  // ===========================================================================

  /** Signal dell'esito per lo strumento indicato. */
  private outcomeSignal(kind: CheckKind) {
    return kind === 'cribis' ? this.cribisOutcome : this.wcOutcome;
  }

  /** Imposta l'esito di un controllo (persistito poi con "Salva controlli"). */
  setOutcome(kind: CheckKind, value: CheckOutcome): void {
    this.outcomeSignal(kind).set(value);
  }

  /** Classe di selezione del bottone esito (vuota se non selezionato). */
  outcomeBtnClass(current: CheckOutcome | null, value: CheckOutcome): string {
    if (current !== value) return '';
    return { OK: 'sel-ok', KO: 'sel-ko', IN_PROGRESS: 'sel-wip' }[value];
  }

  /** Roving tabindex: è tabbabile il valore selezionato; se nessuno, il primo. */
  outcomeTabIndex(current: CheckOutcome | null, value: CheckOutcome): number {
    if (current) return current === value ? 0 : -1;
    return value === this.outcomeOptions[0].value ? 0 : -1;
  }

  /** Classe del tag di stato esito. */
  outcomeTagClass(current: CheckOutcome | null): string {
    return current ? { OK: 't-ok', KO: 't-ko', IN_PROGRESS: 't-wip' }[current] : 't-none';
  }

  /** Etichetta del tag di stato esito. */
  outcomeTagLabel(current: CheckOutcome | null): string {
    return current
      ? { OK: 'OK', KO: 'KO', IN_PROGRESS: 'Approfondimenti in corso' }[current]
      : 'Non valutato';
  }

  /** Navigazione da tastiera del radiogroup esito (frecce, Home/End). */
  onOutcomeKey(ev: KeyboardEvent, kind: CheckKind): void {
    const values = this.outcomeOptions.map(o => o.value);
    const current = values.indexOf(this.outcomeSignal(kind)() as CheckOutcome);
    let next = -1;
    switch (ev.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current < 0 ? 0 : (current + 1) % values.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current < 0 ? values.length - 1 : (current - 1 + values.length) % values.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = values.length - 1;
        break;
      default:
        return;
    }
    ev.preventDefault();
    this.setOutcome(kind, values[next]);
    const group = ev.currentTarget as HTMLElement;
    const buttons = group.querySelectorAll<HTMLButtonElement>('button[role="radio"]');
    buttons[next]?.focus();
  }

  /** Carica dal backend i controlli del fiduciario (note, data, esiti, allegati). */
  private loadReviewControl(professionalId: string): void {
    this.reviewControlService.getReviewControl(professionalId).subscribe({
      next: (control) => this.applyReviewControl(control),
      error: (error) => console.error('Error loading review control:', error),
    });
  }

  /** Applica ai signal del componente lo stato dei controlli ricevuto dal backend. */
  private applyReviewControl(control: ReviewControl): void {
    this.internalNotes.set(control.notes ?? '');
    this.verificationDate.set(this.fromIsoDate(control.verificationDate));
    this.cribisOutcome.set(control.cribisOutcome ?? null);
    this.wcOutcome.set(control.wcOutcome ?? null);
    this.controlDocuments.set(control.documents ?? []);
  }

  /**
   * Salva i dati di controllo (note interne, data verifica, esiti Cribis/WC) sul backend.
   */
  saveControls(): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;

    const request: ReviewControlRequest = {
      notes: this.internalNotes()?.trim() || null,
      verificationDate: this.toIsoDate(this.verificationDate()),
      cribisOutcome: this.cribisOutcome(),
      wcOutcome: this.wcOutcome(),
    };

    this.isSavingControls.set(true);
    this.reviewControlService.upsertReviewControl(professionalId, request).subscribe({
      next: (control) => {
        this.isSavingControls.set(false);
        this.applyReviewControl(control);
        const now = new Date();
        const hh = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        this.controlsSavedMeta.set(`Salvato alle ${hh}:${mm}`);
        this.messageService.add({
          severity: 'success',
          summary: 'Controlli salvati',
          detail: 'Note interne, data verifica ed esiti aggiornati.',
          life: 3000,
        });
      },
      error: (error) => {
        this.isSavingControls.set(false);
        console.error('Error saving review control:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Errore durante il salvataggio dei controlli',
        });
      },
    });
  }

  /**
   * Upload di un allegato ai controlli (evento dal componente documents-manager):
   * validazione client (PDF, 100 MB) e POST con titolo.
   */
  async onControlDocumentUpload(event: { file: File; title: string }): Promise<void> {
    const professionalId = this.professionalId();
    if (!professionalId) return;
    const { file, title } = event;

    const validation = await this.fileValidationService.validateReviewDocument(file);
    if (!validation.valid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validazione file fallita',
        detail: validation.error ?? `"${file.name}" non ha superato i controlli di sicurezza`,
        life: 6000,
      });
      return;
    }

    this.isAddingDocument.set(true);
    try {
      const document = await firstValueFrom(
        this.reviewControlService.uploadDocument(professionalId, file, title)
      );
      this.controlDocuments.update(list => [...list, document]);
      this.messageService.add({
        severity: 'success',
        summary: 'Allegato caricato',
        detail: `"${file.name}" aggiunto`,
        life: 3000,
      });
    } catch (error: any) {
      console.error('Error uploading document:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore caricamento',
        detail: error?.error?.message || `Errore durante il caricamento di "${file.name}"`,
        life: 6000,
      });
    } finally {
      this.isAddingDocument.set(false);
    }
  }

  /** Download di un allegato (evento dal componente documents-manager). */
  onControlDocumentDownload(item: DocManagerItem): void {
    const doc = item.raw as ReviewControlDocument | undefined;
    if (doc?.fileUrl) {
      this.openLink(doc.fileUrl);
    }
  }

  /** Rimozione di un allegato (evento dal componente documents-manager). */
  onControlDocumentRemove(item: DocManagerItem): void {
    this.removeControlDocument(Number(item.id));
  }

  /** Rimuove un allegato dei controlli (anche lato backend/S3). */
  removeControlDocument(id: number): void {
    const professionalId = this.professionalId();
    if (!professionalId) return;
    this.reviewControlService.deleteDocument(professionalId, id).subscribe({
      next: () => {
        this.controlDocuments.update(list => list.filter(d => d.id !== id));
        this.messageService.add({ severity: 'info', summary: 'Allegato rimosso', life: 2500 });
      },
      error: (error) => {
        console.error('Error deleting document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error.error?.message || 'Errore durante la rimozione dell\'allegato',
        });
      },
    });
  }

  /** Converte una Date in stringa ISO yyyy-MM-dd (data locale, senza shift di timezone). */
  private toIsoDate(date: Date | null): string | null {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Converte una stringa ISO yyyy-MM-dd in Date (mezzanotte locale). */
  private fromIsoDate(value: string | null): Date | null {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  /**
   * Navigate back to dashboard
   */
  goBack(): void {
    this.router.navigate(['/axa/dashboard']);
  }

  /**
   * Handle tab change
   */
  onTabChange(value: string | number | undefined): void {
    if (typeof value === 'string') {
      this.activeTabIndex.set(parseInt(value, 10));
    } else if (typeof value === 'number') {
      this.activeTabIndex.set(value);
    }
  }

  /**
   * Decodifica il codice tipologia usando il servizio centralizzato FormDropdownService
   * Get human-readable label for tipologia
   */
  getTipologiaLabel(tipologia: string | undefined): string {
    return this.formDropdownService.decodeProfessionalTypeSync(tipologia);
  }

  /**
   * Get human-readable label for registration step
   */
  getStepLabel(step: string): string {
    const labels: Record<string, string> = {
      'PORTAL_ACCESS': 'Primo Accesso al Portale',
      'DATA_INPUT': 'Inserimento Dati',
      'VERIFICATION': 'Verifica',
      'MFA_SETUP': 'Dati MFA (Codici SOL)',
      'CONTRACT_SENT': 'Contratto Inviato',
      'CONTRACT_SIGNATURE': 'Firma Contratto',
      'CONTRACT_COUNTERSIGNATURE': 'Controfirma Contratto',
      'CONTRACT_ACTIVATED': 'Contratto Attivato',
    };
    return labels[step] || step;
  }

  /**
   * Get human-readable label for registration status
   */
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'PENDING': 'In attesa',
      'USER_INPUT': 'Inserimento utente',
      'COMPLETED': 'Completato',
      'REJECTED': 'Respinto',
      'MODIFICATION_REQUESTED': 'Modifica richiesta',
    };
    return labels[status] || status;
  }

  getProfessionalStatusLabel(status: string | null | undefined): string {
    if (!status) return 'Non disponibile';

    const labels: Record<string, string> = {
      'ACTIVE': 'Attiva',
      'SUSPENDED': 'Sospesa',
      'CEASED': 'Cessata',
      'IN_PROGRESS': 'In lavorazione',
      'AWAITING_RESPONSE': 'In attesa',
      'PENDING': 'In attesa',
      'REGISTRATION': 'Da attivare',
      'PROCESSING': 'Da attivare',
      'MODIFICATION_REQUESTED': 'Modifica richiesta',
    };

    return labels[status] || status;
  }

  /**
   * Attore che ha COMPIUTO l'azione registrata nella riga di storico (non il destinatario).
   * Nel modello append-only ogni riga è creata da una transizione: l'attore è quindi chi ha
   * eseguito quella transizione, e per uno stesso step può cambiare a seconda dello status.
   *  - VERIFICATION PENDING nasce dall'INVIO della domanda (fiduciario); approvazione/modifica/rigetto = Back Office.
   *  - MFA_SETUP PENDING nasce dall'INVIO dei dati MFA (fiduciario); richiesta/modifica/validazione = Back Office.
   *  - CONTRACT_SIGNATURE USER_INPUT nasce dal "contratto pronto" (Back Office); la firma (COMPLETED) = fiduciario.
   *  - CONTRACT_COUNTERSIGNATURE PENDING nasce dalla FIRMA (fiduciario); la controfirma (COMPLETED) = Back Office.
   */
  getHistoryActor(
    step: string | null | undefined,
    status?: string | null | undefined,
  ): 'PROFESSIONAL' | 'BACKOFFICE' | null {
    const st = (status || '').toUpperCase();
    switch ((step || '').toUpperCase()) {
      case 'PORTAL_ACCESS':
      case 'DATA_INPUT':
        // Primo accesso e compilazione/invio anagrafica: sempre il fiduciario.
        return 'PROFESSIONAL';
      case 'VERIFICATION':
        // "In attesa di verifica" è generata dall'invio del fiduciario; il resto è del Back Office.
        return st === 'PENDING' ? 'PROFESSIONAL' : 'BACKOFFICE';
      case 'MFA_SETUP':
        // Invio dati MFA (PENDING) = fiduciario; richiesta compilazione/modifica e validazione = Back Office.
        return st === 'PENDING' ? 'PROFESSIONAL' : 'BACKOFFICE';
      case 'CONTRACT_SIGNATURE':
        // Firma effettiva (COMPLETED) = fiduciario; la messa a disposizione del contratto = Back Office.
        return st === 'COMPLETED' ? 'PROFESSIONAL' : 'BACKOFFICE';
      case 'CONTRACT_COUNTERSIGNATURE':
        // "In attesa di controfirma" (PENDING) è generata dalla firma del fiduciario; la controfirma = Back Office.
        return st === 'PENDING' ? 'PROFESSIONAL' : 'BACKOFFICE';
      case 'CONTRACT_SENT':
      case 'CONTRACT_ACTIVATED':
        // Approvazione MFA/invio contratto e attivazione finale: Back Office.
        return 'BACKOFFICE';
      default:
        return null;
    }
  }

  getHistoryActorIcon(step: string | null | undefined, status?: string | null | undefined): string {
    const actor = this.getHistoryActor(step, status);
    return actor === 'PROFESSIONAL' ? 'pi-user'
      : actor === 'BACKOFFICE' ? 'pi-briefcase'
      : '';
  }

  getHistoryActorDisplayLabel(
    step: string | null | undefined,
    status: string | null | undefined,
  ): string {
    const actor = this.getHistoryActor(step, status);

    return actor === 'PROFESSIONAL'
      ? `Fiduciario`
      : actor === 'BACKOFFICE'
        ? `Utente Backoffice`
        : '';
  }

  /**
   * Get color for registration status
   */
  getStatusColor(status: string): string {
    return this.registrationStepService.getStatusColor(status as RegistrationStatus);
  }

  /**
   * Format date and time
   */
  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Rome',
    });
  }

  /**
   * Handle contract detail click
   */
  onContractDetailClick(contract: any): void {
    if (contract?.contractId) {
      this.router.navigate(['/axa/professional-contracts-registry', contract.contractId]);
    }
  }

  /**
   * Handle contract edit click
   */
  onContractEditClick(contract: any): void {
    if (contract?.contractId) {
      this.router.navigate(['/axa/professional-contract-registration-wizard', contract.contractId]);
    }
  }

  /**
   * Navigate to contract creation wizard with professional ID pre-filled
   */
  createContract(): void {
    const professionalId = this.professionalId();
    if (!professionalId) {
      console.error('❌ Cannot create contract: no professional ID');
      return;
    }

    // Use the explicit professional route so the wizard resolves the domain from the path,
    // not from the current user role.
    this.router.navigate(['/axa/professional-contract-registration-wizard'], {
      queryParams: { professionalId }
    });
  }

  /**
   * Naviga al contratto già in fase di definizione (bozza / da attivare) per proseguirne la compilazione.
   */
  continueContract(): void {
    const contract = this.inDefinitionContract();
    if (contract?.contractId) {
      this.router.navigate(['/axa/professional-contract-registration-wizard', contract.contractId]);
    }
  }

  /**
   * Apre il dettaglio del contratto di riferimento (dove il BO gestisce firma/controfirma e attivazione).
   */
  goToPrimaryContractDetail(): void {
    const contract = this.primaryContract();
    if (contract?.contractId) {
      this.router.navigate(['/axa/professional-contracts-registry', contract.contractId]);
    }
  }
}
